<template>
  <div class="furniture-selector" v-if="!loading">
    <div class="furniture-selector__search-bar" v-if="displaySearchBar">
      <el-input class="grey-input furniture-selector__search-bar__input"
                placeholder="Rechercher"
                suffix-icon="el-icon-search"
                v-model="searchText"
                @keyup.native="!isDesktop && keyup($event.target.value)"
      />
      <div class="furniture-selector__search-bar__action">
        <div class="furniture-selector__search-bar__action--add"
             @click="onDisplayCustomFurnitureModal">
          <font-awesome-icon :icon="['fal', 'plus-square']" />
          {{$t('inventory.furnitures.addCustomFurniture')}}
        </div>
        <el-switch
          class="furniture-selector__search-bar__action--switch"
          v-model="displayCustomerFurniture"
          :active-text="$t('inventory.displayCustomerFurniture')">
        </el-switch>
      </div>
    </div>
    <furniture-selector-body :furniture-category="furnitureKind"
                             :furnitures="furnitures"
                             :display-category-selector="searchText.length === 0"
                             @update:category="updateCategory"
                             @update:furniture="updateFurniture"
                             @update:disassembly="updateFurniture"
                             :selectCategory="selectCategory"
                             :is-search-bar-open="displaySearchBar"
                             :init="init"
    />
    <custom-furniture-modal v-model="customFurniture"
                            :visible="displayCustomFurnitureModal"
                            @visibilityChange="onCustomFurnitureModalChange"
                            @create-furniture="createCustomFurniture"
    />
  </div>
  <div class="loading loading__absolute" v-else>
    <Loader></Loader>
  </div>
</template>
<script>

import { mapGetters } from 'vuex';
import FurnitureSelectorBody from './FurnitureSelectorBody.vue';
import Loader from '@/components/utils/Loader.vue';

import CustomFurnitureModal from '@/components/furnitures/modals/CustomFurnitureModal.vue';

export default {
  name: 'furniture-selector',
  components: { CustomFurnitureModal, FurnitureSelectorBody, Loader },
  data() {
    return {
      activeStandard: 0,
      displayCustomerFurniture: false,
      searchText: '',
      selectCategory: 0,
      customFurniture: {
        nom_type: '',
        width: 0,
        length: 0,
        height: 0,
        fragile: false,
      },
      displayCustomFurnitureModal: false,
      customFurnitures: [],
    };
  },
  props: {
    roomFurniture: {
      type: Array,
      required: true,
    },
    autoDisassembly: {
      type: Boolean,
      default: false,
    },
    displaySearchBar: {
      type: Boolean,
      default: true,
    },
    init: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      isDesktop: 'dimensionStore/isDesktop',
    }),
    standardFurniture() {
      const res = [...this.$store.getters['furnituresStore/furnituresList']];
      if (res.length > 0) res.push({ name: 'Meubles personnalisés', meubles_type: this.customsFnt });
      return res;
    },
    customsFnt() {
      const res = [];
      const userCustomFurniture = this.roomFurniture
        .filter(f => f.icon === 'unknown');
      res.push(...userCustomFurniture);
      return res;
    },
    furnitureKind() {
      if (this.searchText.length !== 0) return [{ name: this.$t('inventory.search') }];
      if (this.displayCustomerFurniture) return [{ name: this.$t('inventory.yourFurniture') }];
      return this.standardFurniture.map(kind => ({ name: kind.name }));
    },
    getStandardFurniture() {
      if (this.standardFurniture[this.activeStandard]?.meubles_type) {
        return this.formatFurniture(this.standardFurniture[this.activeStandard]?.meubles_type);
      }
      return [];
    },
    furnitures() {
      if (this.searchText.length !== 0) return this.searchSelection;
      if (this.displayCustomerFurniture) return this.roomFurniture;
      return this.getStandardFurniture;
    },
    searchSelection() {
      const result = [];
      for (const standard of this.standardFurniture) {
        const furniture = standard.meubles_type;
        const matchingFurniture = furniture
          .filter(f => f.nom_type?.toLowerCase().includes(this.searchText.toLowerCase())
          || f.name?.toLowerCase().includes(this.searchText.toLowerCase()));
        result.push(...this.formatFurniture(matchingFurniture));
      }
      return result;
    },
  },
  methods: {
    keyup(value) {
      this.searchText = value;
    },
    formatFurniture(furnitures) {
      const furnitureType = furnitures.map((type) => {
        if (type.icon !== 'unknown') {
          type.quantity = 0;
          type.demontage = false;
        } else {
          type.icon = 'unknown';
        }
        return Object.assign({}, type);
      }) || [];

      for (const furniture of this.roomFurniture) {
        const furnitureTypeIndex = furnitureType
          .findIndex(f => f.nom_type === furniture.name);
        if (furnitureTypeIndex !== -1) {
          const furnitureQty = parseInt(furniture.quantity, 10);
          furnitureType[furnitureTypeIndex].quantity = furnitureQty;
          furnitureType[furnitureTypeIndex].demontage = furnitureQty > 0
            ? furniture.demontage
            : false;
        }
      }
      return furnitureType
        .sort((a, b) => a.nom_type && a.nom_type.localeCompare(b.nom_type));
    },
    updateCategory(index) {
      this.activeStandard = index;
    },
    updateFurniture(event) {
      if (event.disassembly === undefined
        && event.furniture.quantity === 0) {
        event.disassembly = this.autoDisassembly;
      }
      this.$emit('update:furniture', event);
    },
    onDisplayCustomFurnitureModal() {
      this.displayCustomFurnitureModal = true;
    },
    customFurnitureVolume() {
      return (this.customFurniture.width
          * this.customFurniture.length
          * this.customFurniture.height)
          / 1000000;
    },
    resetCustomFurniture() {
      this.customFurniture.nom_type = '';
      this.customFurniture.height = 0;
      this.customFurniture.width = 0;
      this.customFurniture.length = 0;
    },
    createCustomFurniture() {
      let furniture = this.customFurnitures
        .find(f => f.nom_type === this.customFurniture.nom_type);
      const { nom_type } = this.customFurniture;

      if (!furniture) {
        furniture = {};
        const volume = this.customFurnitureVolume().toFixed(1);
        this.$set(furniture, 'nom_type', nom_type);
        this.$set(furniture, 'quantity', 1);
        this.$set(furniture, 'icon', 'unknown');
        this.$set(furniture, 'type', null);
        this.$set(furniture, 'demontage_type', true);
        this.$set(furniture, 'length', this.customFurniture.length);
        this.$set(furniture, 'width', this.customFurniture.width);
        this.$set(furniture, 'height', this.customFurniture.height);
        this.$set(furniture, 'volume_type', volume >= 0.1 ? volume : 0.1);
        this.customFurnitures.push(furniture);
      }

      this.updateFurniture({
        furniture,
        quantity: 1,
      });

      this.displayCustomFurnitureModal = false;
      this.resetCustomFurniture();
      this.selectCategory = this.furnitureKind.length - 1;
    },
    onCustomFurnitureModalChange(e) {
      this.displayCustomFurnitureModal = e;
      if (e === false) this.resetCustomFurniture();
    },
  },
  watch: {
    displaySearchBar(value) {
      if (!value) {
        this.searchText = '';
      }
    },
  },
};
</script>
<style scoped lang="scss">
  .loading__absolute {
    position: absolute;
  }
  .furniture-selector {
    border: none;

    @include breakpoint(desktop) {
      border: 1px solid $--color-text-secondary;
      border-radius: $--border-radius-base;
      height: fit-content;
    }
    &__search-bar {
      display: grid;
      border: 1px solid $--color-text-secondary;
      border-width: 1px 0;
      column-gap: 10px;
      padding: 10px 20px;
      margin: 0 -20px;
      border-radius: 0;
      align-items: center;
      grid-template-columns: 100%;
      height: 110px;

      @include breakpoint(desktop) {
        border-top: none;
        grid-template-columns: minmax(100px, 500px) auto;
        margin: 0;
        border-radius: $--border-radius-base $--border-radius-base 0 0;
        height: 60px;
      }
      &__action {
        display: flex;

        &--switch {
          align-self: center;
        }

        &--add {
          display: grid;
          grid-template-columns: 30px 1fr;
          place-items: center;
          place-self: center;
          align-items: center;
          margin-right: 10px;
          font-weight: 500;
          line-height: 1;
          font-size: $--font-size-small;
          align-self: center;
          cursor: pointer;
        }
      }
    }

  }
</style>
